import jwtDecode from "jwt-decode";

/**
 * Acesso centralizado às chaves de autenticação.
 *
 * Fase 2 (modelo de 2 tokens) — ADITIVO e seguro:
 *  - **access token** (JWT 30min, identidade Master/Roles/Rights + exp): vive em
 *    MEMÓRIA (variável de módulo) + espelho em `sessionStorage` (sobrevive a um
 *    reload de aba enquanto válido). NUNCA em localStorage.
 *  - **refresh token** (opaco, rotativo): `localStorage` quando "lembrar-me"
 *    (rememberMe), senão `sessionStorage`.
 *  - **companyStatus / activationStatus** (JWTs de status, mesmo formato de hoje):
 *    recriados no login e a cada refresh.
 *
 * Compatibilidade: os métodos legados (getUserData/getToken/getCompanyStatusToken
 * etc., todos sobre localStorage) seguem funcionando. `getAccessToken()` cai no
 * token legado (`userData.userToken`) quando ainda não houver access novo — assim
 * a migração dos thunks para o cliente `api` é segura ANTES da virada do login
 * (Fase B). Origem: TD-014 + Auth Fase 2.
 */

const KEYS = {
	USER_DATA: "@dream4/userData",          // legado (token "tudo-em-um")
	COMPANY_STATUS: "@dream4/companyStatus",
	ACTIVATION_STATUS: "@dream4/activationStatus",
	LOGGED: "@dream4/logged",
	ACCESS: "@dream4/accessToken",           // espelho em sessionStorage
	REFRESH: "@dream4/refreshToken",
	REMEMBER: "@dream4/rememberMe",
};

const safeParse = (raw) => {
	if (!raw) return null;
	try { return JSON.parse(raw); } catch { return null; }
};
const safeDecode = (token) => {
	if (!token) return null;
	try { return jwtDecode(token); } catch { return null; }
};
// Acesso defensivo (sessionStorage pode lançar em modo restrito).
const ss = {
	get: (k) => { try { return sessionStorage.getItem(k); } catch { return null; } },
	set: (k, v) => { try { sessionStorage.setItem(k, v); } catch { /* noop */ } },
	del: (k) => { try { sessionStorage.removeItem(k); } catch { /* noop */ } },
};

// Access token em memória (preferencial). Espelho em sessionStorage p/ reload.
let _accessToken = null;

export const authStorage = {
	// ----------------------------------------------------------------------
	// ACCESS TOKEN (Fase 2) — memória + espelho sessionStorage
	// ----------------------------------------------------------------------
	setAccessToken: (token) => {
		_accessToken = token || null;
		if (token) ss.set(KEYS.ACCESS, token); else ss.del(KEYS.ACCESS);
	},
	/** Access novo (memória/sessionStorage) com FALLBACK ao token legado. */
	getAccessToken: () => _accessToken || ss.get(KEYS.ACCESS) || authStorage.getToken(),
	/**
	 * Access EXCLUSIVO desta aba (memória + sessionStorage), SEM fallback ao
	 * localStorage legado. Usado para decidir "esta aba tem sessão própria?".
	 */
	getTabAccessToken: () => _accessToken || ss.get(KEYS.ACCESS) || null,
	/**
	 * Há sessão NESTA aba? (access próprio da aba OU refresh disponível p/ renovar).
	 * Base do modelo per-tab: o @dream4/userData compartilhado no localStorage NÃO
	 * conta como sessão de uma aba nova (evita o falso "chave de acesso alterada").
	 */
	hasTabSession: () => !!authStorage.getTabAccessToken() || authStorage.hasRefreshToken(),
	getDecodedAccessToken: () => safeDecode(authStorage.getAccessToken()),
	/** exp (epoch ms) do access atual, ou null. */
	getAccessExpiryMs: () => {
		const dec = authStorage.getDecodedAccessToken();
		return dec && dec.exp ? dec.exp * 1000 : null;
	},

	// ----------------------------------------------------------------------
	// REFRESH TOKEN (Fase 2) — localStorage (lembrar) ou sessionStorage
	// ----------------------------------------------------------------------
	setRefreshToken: (token, remember) => {
		// limpa dos dois stores antes de gravar no correto
		try { localStorage.removeItem(KEYS.REFRESH); } catch { /* noop */ }
		ss.del(KEYS.REFRESH);
		if (!token) return;
		if (remember) localStorage.setItem(KEYS.REFRESH, token);
		else ss.set(KEYS.REFRESH, token);
	},
	getRefreshToken: () => {
		try {
			return localStorage.getItem(KEYS.REFRESH) || ss.get(KEYS.REFRESH) || null;
		} catch {
			return ss.get(KEYS.REFRESH) || null;
		}
	},
	hasRefreshToken: () => !!authStorage.getRefreshToken(),

	// ----------------------------------------------------------------------
	// remember-me
	// ----------------------------------------------------------------------
	setRememberMe: (bool) => {
		try { localStorage.setItem(KEYS.REMEMBER, bool ? "1" : "0"); } catch { /* noop */ }
	},
	isRememberMe: () => {
		try { return localStorage.getItem(KEYS.REMEMBER) === "1"; } catch { return false; }
	},

	/**
	 * Grava a sessão completa retornada por /auth/login ou /auth/refresh.
	 * Os tokens de status mantêm leitura legada (localStorage) — são recriados
	 * a cada chamada, então sobrescrever é seguro.
	 */
	setSession: ({ accessToken, refreshToken, companyStatusToken, activationStatusToken, rememberMe } = {}) => {
		if (rememberMe !== undefined) authStorage.setRememberMe(!!rememberMe);
		const remember = rememberMe !== undefined ? !!rememberMe : authStorage.isRememberMe();
		if (accessToken !== undefined) {
			authStorage.setAccessToken(accessToken);
			// Interino (B.1): manter o @dream4/userData legado com o access fresco,
			// p/ os leitores atuais (layouts decodificam userData.userToken; socket
			// reenvia esse token). Some na Fase B.2 (leitores migram p/ authStorage).
			const ud = authStorage.getUserData();
			if (ud) { ud.userToken = accessToken; authStorage.setUserData(ud); }
		}
		if (refreshToken !== undefined) authStorage.setRefreshToken(refreshToken, remember);
		if (companyStatusToken) authStorage.setCompanyStatusToken(companyStatusToken);
		if (activationStatusToken) authStorage.setActivationStatusToken(activationStatusToken);
		authStorage.setLogged("1");
	},

	// ----------------------------------------------------------------------
	// LEGADO (token "tudo-em-um") — mantido para compatibilidade/transição
	// ----------------------------------------------------------------------
	getUserData: () => safeParse(localStorage.getItem(KEYS.USER_DATA)),
	setUserData: (obj) => localStorage.setItem(KEYS.USER_DATA, JSON.stringify(obj)),
	getToken: () => authStorage.getUserData()?.userToken ?? null,
	getDecodedToken: () => safeDecode(authStorage.getToken()),

	// --- companyStatus token ---
	getCompanyStatusToken: () => localStorage.getItem(KEYS.COMPANY_STATUS),
	setCompanyStatusToken: (token) => localStorage.setItem(KEYS.COMPANY_STATUS, token),
	getDecodedCompanyStatus: () => safeDecode(authStorage.getCompanyStatusToken()),

	// --- activationStatus token ---
	getActivationStatusToken: () => localStorage.getItem(KEYS.ACTIVATION_STATUS),
	setActivationStatusToken: (token) => localStorage.setItem(KEYS.ACTIVATION_STATUS, token),
	getDecodedActivationStatus: () => safeDecode(authStorage.getActivationStatusToken()),

	// --- flag de sessão ativa ("0" ou "1") ---
	getLogged: () => localStorage.getItem(KEYS.LOGGED),
	setLogged: (value) => localStorage.setItem(KEYS.LOGGED, value),
	isLogged: () => authStorage.getLogged() === "1",

	// ----------------------------------------------------------------------
	// limpeza completa (logout)
	// ----------------------------------------------------------------------
	clearAll: () => {
		_accessToken = null;
		ss.del(KEYS.ACCESS);
		ss.del(KEYS.REFRESH);
		Object.values(KEYS).forEach((k) => { try { localStorage.removeItem(k); } catch { /* noop */ } });
	},

	KEYS,
};

export default authStorage;
